// src/contexts/GameContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import gameService from '../services/gameService';
import websocketService from '../services/websocketService';

const GameContext = createContext();

// 初始状态
const initialState = {
  // WebSocket 连接状态
  wsStatus: 'disconnected',

  // 房间状态
  currentRoom: null,
  isInRoom: false,

  // 游戏状态
  gameState: null,
  isPlaying: false,

  // UI 状态
  selectedCards: {
    market: [],
    hand: [],
    camels: []
  },
  error: null
};

// Reducer
function gameReducer(state, action) {
  switch (action.type) {
    case 'SET_WS_STATUS':
      return { ...state, wsStatus: action.payload };

    case 'ROOM_CREATED':
    case 'ROOM_JOINED':
      return {
        ...state,
        currentRoom: action.payload,
        isInRoom: true,
        error: null
      };

    case 'ROOM_UPDATED':
      return {
        ...state,
        currentRoom: action.payload
      };

    case 'ROOM_LEFT':
      return {
        ...state,
        currentRoom: null,
        isInRoom: false,
        gameState: null,
        isPlaying: false
      };

    case 'GAME_STARTED':
      return {
        ...state,
        gameState: null,
        isPlaying: true,
        selectedCards: {
          market: [],
          hand: [],
          camels: []
        }
      };

    case 'GAME_STATE_UPDATED':
      return {
        ...state,
        gameState: action.payload,
        isPlaying: action.payload ? action.payload.status === 'playing' : false
      };

    case 'GAME_OVER':
      return {
        ...state,
        gameState: action.payload,
        isPlaying: false
      };

    case 'RESET_GAME':
      return {
        ...state,
        gameState: null,
        isPlaying: false,
        selectedCards: {
          market: [],
          hand: [],
          camels: []
        }
      };

    case 'SELECT_MARKET_CARD':
      return {
        ...state,
        selectedCards: {
          ...state.selectedCards,
          market: state.selectedCards.market.includes(action.payload)
            ? state.selectedCards.market.filter(idx => idx !== action.payload)
            : [...state.selectedCards.market, action.payload]
        }
      };

    case 'SELECT_HAND_CARD':
      return {
        ...state,
        selectedCards: {
          ...state.selectedCards,
          hand: state.selectedCards.hand.includes(action.payload)
            ? state.selectedCards.hand.filter(idx => idx !== action.payload)
            : [...state.selectedCards.hand, action.payload]
        }
      };

    case 'SELECT_CAMEL':
      return {
        ...state,
        selectedCards: {
          ...state.selectedCards,
          camels: state.selectedCards.camels.includes(action.payload)
            ? state.selectedCards.camels.filter(idx => idx !== action.payload)
            : [...state.selectedCards.camels, action.payload]
        }
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedCards: {
          market: [],
          hand: [],
          camels: []
        }
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  useEffect(() => {
    // 监听 WebSocket 连接状态
    const unsubscribeStatus = websocketService.addStatusListener((status) => {
      dispatch({ type: 'SET_WS_STATUS', payload: status });
      if (status === 'auth_error') {
        dispatch({ type: 'SET_ERROR', payload: '认证失败，请重新登录' });
      }
    });

    // 监听房间消息
    const unsubscribeRoom = gameService.addRoomListener(({ action, data }) => {
      console.log('Room event:', action, data);

      switch (action) {
        case 'room.created':
          dispatch({ type: 'ROOM_CREATED', payload: data.room?.data?.room });
          break;

        case 'room.joined':
          dispatch({ type: 'ROOM_JOINED', payload: data.room?.data?.room });
          break;

        case 'room.player_joined':
        case 'room.ready_updated':
          dispatch({ type: 'ROOM_UPDATED', payload: data.room?.data?.room });
          break;

        case 'room.player_left':
          const updatedRoom = data.room?.data?.room;
          dispatch({ type: 'ROOM_UPDATED', payload: updatedRoom });
          // 如果房间状态变回 waiting，清除游戏状态
          if (updatedRoom && updatedRoom.status === 'waiting') {
            dispatch({ type: 'RESET_GAME' });
          }
          break;

        case 'room.game_started':
          dispatch({ type: 'GAME_STARTED' });
          dispatch({ type: 'ROOM_UPDATED', payload: data.room?.data?.room });
          break;

        case 'room.error':
          const errorMsg = data.room?.data?.error_msg || '房间操作失败';
          dispatch({ type: 'SET_ERROR', payload: errorMsg });
          setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 3000);
          break;

        default:
          break;
      }
    });

    // 监听游戏消息
    const unsubscribeGame = gameService.addGameListener(({ action, data }) => {
      console.log('Game event:', action, data);

      switch (action) {
        case 'game.event.state_update':
          dispatch({ type: 'GAME_STATE_UPDATED', payload: data.game?.data?.game_state });
          dispatch({ type: 'CLEAR_SELECTION' });
          break;

        case 'game.event.game_over':
          dispatch({ type: 'GAME_OVER', payload: data.game?.data?.game_state });
          break;

        case 'game.event.error':
          const errorMsg = data.game?.data?.error_msg || '游戏操作失败';
          dispatch({ type: 'SET_ERROR', payload: errorMsg });
          setTimeout(() => dispatch({ type: 'CLEAR_ERROR' }), 3000);
          break;

        default:
          break;
      }
    });

    return () => {
      unsubscribeStatus();
      unsubscribeRoom();
      unsubscribeGame();
    };
  }, []);

  // 连接 WebSocket
  const connect = async () => {
    await websocketService.connect();
  };

  // 断开 WebSocket
  const disconnect = () => {
    websocketService.disconnect();
  };

  // 房间操作
  const createRoom = (nickname) => {
    gameService.createRoom(nickname);
  };

  const joinRoom = (roomId, nickname) => {
    gameService.joinRoom(roomId, nickname);
  };

  const leaveRoom = () => {
    gameService.leaveRoom();
    dispatch({ type: 'ROOM_LEFT' });
  };

  const setReady = (isReady) => {
    gameService.setReady(isReady);
  };

  const resetGame = () => {
    dispatch({ type: 'RESET_GAME' });
  };

  const returnToLobby = () => {
    setReady(false);        // 通知服务器取消准备状态
    dispatch({ type: 'RESET_GAME' });  // 清除前端游戏状态
  };

  // 游戏操作
  const takeOneCard = (marketIndex) => {
    gameService.takeOneCard(marketIndex);
  };

  const takeAllCamels = () => {
    gameService.takeAllCamels();
  };

  const exchangeCards = (marketIndices, handCards, useCamels) => {
    gameService.exchangeCards(marketIndices, handCards, useCamels);
  };

  const sellCards = (cardType, count) => {
    gameService.sellCards(cardType, count);
  };

  // 选择操作
  const selectMarketCard = (index) => {
    dispatch({ type: 'SELECT_MARKET_CARD', payload: index });
  };

  const selectHandCard = (index) => {
    dispatch({ type: 'SELECT_HAND_CARD', payload: index });
  };

  const selectCamel = (index) => {
    dispatch({ type: 'SELECT_CAMEL', payload: index });
  };

  const clearSelection = () => {
    dispatch({ type: 'CLEAR_SELECTION' });
  };

  const value = {
    // 状态
    ...state,

    // WebSocket 操作
    connect,
    disconnect,

    // 房间操作
    createRoom,
    joinRoom,
    leaveRoom,
    setReady,
    resetGame,
    returnToLobby,

    // 游戏操作
    takeOneCard,
    takeAllCamels,
    exchangeCards,
    sellCards,

    // 选择操作
    selectMarketCard,
    selectHandCard,
    selectCamel,
    clearSelection
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}
